/**
 * live-preview-history.js — Undo / Redo for everything the customer does to
 * the cover: typing, the AI Sidekick's insertions, font, size, alignment,
 * colour. Two icon buttons in the controls row, beside − Aa +.
 *
 * Asked for by Stelios (2026-09-11): «να λειτουργεί για τα πάντα … 2 buttons
 * UNDO & REDO». It replaces the Sidekick's own one-shot «↩ Αναίρεση».
 *
 * HOW IT RECORDS. It never hooks into live-preview.js. It listens on the
 * element in the CAPTURE phase — so it sees a click on a control or a change
 * of the font before the element's own handler runs — and takes a snapshot
 * of the element's public state right after that handler has finished
 * (setTimeout 0). Typing is grouped: keystrokes closer than 700 ms are one
 * step, so undo takes back a word or a pause's worth, not one letter. A click
 * on any other control flushes the pending typing first, so «typed, then
 * changed font» is two steps in the right order.
 *
 * HOW IT RESTORES. Through the same doors a customer uses: the textarea's
 * value + an `input` event, the font <select>'s value + `change`,
 * `setAlign()`, and the two fields the size stepper and the colour picker
 * write (`userSizePct`/`sizeTouched`, `userColor`) followed by one
 * `renderForUser()`. So the fit, the floor, the refusal, the descriptor and
 * the gallery all follow exactly as if the customer had done it by hand — and
 * the health monitor sees nothing unusual.
 *
 * What it deliberately does NOT do: bind Ctrl+Z / Ctrl+Y. Inside the textarea
 * those belong to the browser's own text undo, which fires `input` events and
 * is therefore recorded here like typing. Two undo systems fighting over one
 * key is worse than two buttons.
 *
 * Analytics: `lp_history` with lp_result undo|redo. Never the text.
 */
(function () {
  'use strict';

  var TYPING_MS = 700;
  var MAX_STEPS = 100;
  var T = { undo: 'Αναίρεση', redo: 'Επανάληψη', group: 'Αναίρεση / Επανάληψη' };
  // Feather icons (MIT): rotate-ccw / rotate-cw — the circular arrows.
  var SVG_UNDO = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>';
  var SVG_REDO = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>';

  function init(el) {
    if (!el || el._lpHistory) return;
    if (el.config && el.config.history === false) return;
    var input = el.input || el.querySelector('[data-lp-input]');
    var row = el.querySelector('.lp-controls-row');
    if (!input || !row) return;
    el._lpHistory = true;

    // ── the two buttons, in the controls row, before − Aa + ───────────────
    var group = document.createElement('div');
    group.className = 'lp-hist';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', T.group);
    group.setAttribute('data-lp-history', '');
    var undoBtn = button('undo', T.undo, SVG_UNDO);
    var redoBtn = button('redo', T.redo, SVG_REDO);
    group.appendChild(undoBtn);
    group.appendChild(redoBtn);
    // before − Aa +; without a stepper, before the helper's pill (it is the row's right end)
    var size = row.querySelector('.lp-size') || row.querySelector('.lp-ai-fab');
    if (size) row.insertBefore(group, size); else row.appendChild(group);

    var past = [];
    var future = [];
    var applying = false;
    var typingTimer = null;

    function snapshot() {
      var f = (el.state && el.state.font) || {};
      return {
        text: input.value || '',
        family: f.family || null,
        align: f.align || null,
        size: el.userSizePct == null ? null : el.userSizePct,
        touched: !!el.sizeTouched,
        color: el.userColor == null ? null : el.userColor,
      };
    }
    function same(a, b) {
      return a.text === b.text && a.family === b.family && a.align === b.align &&
        a.size === b.size && a.touched === b.touched && a.color === b.color;
    }
    var current = snapshot(); // the baseline — undo can go all the way back to it

    function commit() {
      if (applying) return;
      var s = snapshot();
      if (same(s, current)) return;
      past.push(current);
      if (past.length > MAX_STEPS) past.shift();
      current = s;
      future = [];
      refresh();
    }
    function flushTyping() {
      if (!typingTimer) return;
      clearTimeout(typingTimer);
      typingTimer = null;
      commit();
    }
    function refresh() {
      undoBtn.disabled = !past.length;
      redoBtn.disabled = !future.length;
    }
    refresh();

    // ── recording ─────────────────────────────────────────────────────────
    el.addEventListener('input', function (e) {
      if (applying || e.target !== input) return;
      if (typingTimer) clearTimeout(typingTimer);
      typingTimer = setTimeout(function () { typingTimer = null; commit(); }, TYPING_MS);
    }, true);
    el.addEventListener('change', function (e) {
      if (applying || e.target === input) return;   // font select, colour picker
      flushTyping();
      setTimeout(commit, 0);
    }, true);
    el.addEventListener('click', function (e) {
      if (applying) return;
      var t = e.target && e.target.closest ? e.target.closest('[data-lp-align-btn],[data-lp-size-dec],[data-lp-size-inc]') : null;
      if (!t) return;
      flushTyping();
      setTimeout(commit, 0);
    }, true);

    // ── restoring ─────────────────────────────────────────────────────────
    function apply(s) {
      applying = true;
      try {
        if (input.value !== s.text) {
          input.value = s.text;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
        var sel = el.fontSelect;
        if (s.family && el.state && el.state.font.family !== s.family) {
          if (sel) {
            sel.value = s.family;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            el.state.font.family = s.family;
          }
        }
        if (s.align && el.state && el.state.font.align !== s.align) {
          if (typeof el.setAlign === 'function') el.setAlign(s.align);
          else el.state.font.align = s.align;
        }
        el.userSizePct = s.size;
        el.sizeTouched = s.touched;
        el.userColor = s.color;
        if (typeof el.renderForUser === 'function') el.renderForUser();
        else if (typeof el.render === 'function') el.render();
      } finally {
        applying = false;
      }
      current = snapshot();
      refresh();
    }
    function undo() {
      flushTyping();
      if (!past.length) return;
      future.push(current);
      apply(past.pop());
      track('undo');
    }
    function redo() {
      flushTyping();
      if (!future.length) return;
      past.push(current);
      apply(future.pop());
      track('redo');
    }
    undoBtn.addEventListener('click', undo);
    redoBtn.addEventListener('click', redo);

    el.lpHistory = { undo: undo, redo: redo, depth: function () { return { past: past.length, future: future.length }; } };
  }

  function button(kind, label, svg) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'lp-size-btn lp-hist-btn';
    b.setAttribute('data-lp-history-' + kind, '');
    b.setAttribute('aria-label', label);
    b.setAttribute('title', label);
    b.innerHTML = svg; // a constant of this file, never anything the page or the model produced
    return b;
  }

  function track(result) {
    var params = { lp_result: result };
    try { if (typeof window.gtag === 'function') window.gtag('event', 'lp_history', params); } catch (e) { /* analytics never breaks the shop */ }
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'lp_history', lp_result: result }); } catch (e) { /* same */ }
  }

  function boot() {
    var all = document.querySelectorAll('live-preview');
    for (var i = 0; i < all.length; i++) init(all[i]);
  }
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('live-preview').then(boot);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.LivePreviewHistory = { init: init };
})();
